import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { resourceTeamAssignmentsApi } from '@/utils/api';
import type { ResourceTeamAssignment } from '@/types';
import type { RootState } from '../index';

interface ResourceTeamAssignmentsState {
  entities: ResourceTeamAssignment[];
  currentEntity: ResourceTeamAssignment | null;
  loading: boolean;
  error: string | null;
}

const initialState: ResourceTeamAssignmentsState = {
  entities: [],
  currentEntity: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchAssignments = createAsyncThunk(
  'resourceTeamAssignments/fetchAll',
  async (params: { resourceId?: string; teamId?: string; active?: boolean } | undefined, { rejectWithValue }) => {
    try {
      const response = await resourceTeamAssignmentsApi.getAll(params);

      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch assignments');
      }
      return (response.data as { assignments: ResourceTeamAssignment[] })?.assignments || [];
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch assignments');
    }
  }
);

export const fetchAssignmentById = createAsyncThunk(
  'resourceTeamAssignments/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await resourceTeamAssignmentsApi.getById(id);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch assignment');
      }
      return (response.data as { assignment: ResourceTeamAssignment })?.assignment || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch assignment');
    }
  }
);

export const createAssignment = createAsyncThunk(
  'resourceTeamAssignments/create',
  async (data: Partial<ResourceTeamAssignment>, { rejectWithValue }) => {
    try {
      const response = await resourceTeamAssignmentsApi.create(data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to create assignment');
      }
      return (response.data as { assignment: ResourceTeamAssignment })?.assignment || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to create assignment');
    }
  }
);

export const updateAssignment = createAsyncThunk(
  'resourceTeamAssignments/update',
  async ({ id, data }: { id: string; data: Partial<ResourceTeamAssignment> }, { rejectWithValue }) => {
    try {
      const response = await resourceTeamAssignmentsApi.update(id, data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to update assignment');
      }
      return (response.data as { assignment: ResourceTeamAssignment })?.assignment || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to update assignment');
    }
  }
);

export const deleteAssignment = createAsyncThunk(
  'resourceTeamAssignments/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await resourceTeamAssignmentsApi.delete(id);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to delete assignment');
      }
      return id;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to delete assignment');
    }
  }
);

// Special thunk for transferring resources between teams
export const transferResource = createAsyncThunk(
  'resourceTeamAssignments/transfer',
  async (
    data: { resourceId: string; newTeamId: string; transferDate: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await resourceTeamAssignmentsApi.transfer(data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to transfer resource');
      }
      return (response.data as { assignment: ResourceTeamAssignment })?.assignment || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to transfer resource');
    }
  }
);

const resourceTeamAssignmentsSlice = createSlice({
  name: 'resourceTeamAssignments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentEntity: (state, action: PayloadAction<ResourceTeamAssignment | null>) => {
      state.currentEntity = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchAssignments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssignments.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload;
      })
      .addCase(fetchAssignments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch by ID
      .addCase(fetchAssignmentById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssignmentById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEntity = action.payload;
      })
      .addCase(fetchAssignmentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create
      .addCase(createAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAssignment.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.entities.unshift(action.payload);
        }
      })
      .addCase(createAssignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update
      .addCase(updateAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAssignment.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const index = state.entities.findIndex(a => a.id === action.payload!.id);
          if (index !== -1) {
            state.entities[index] = action.payload;
          }
          if (state.currentEntity?.id === action.payload.id) {
            state.currentEntity = action.payload;
          }
        }
      })
      .addCase(updateAssignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deleteAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = state.entities.filter(a => a.id !== action.payload);
        if (state.currentEntity?.id === action.payload) {
          state.currentEntity = null;
        }
      })
      .addCase(deleteAssignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Transfer resource
      .addCase(transferResource.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(transferResource.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          // Add the new assignment to the list
          state.entities.unshift(action.payload);
        }
      })
      .addCase(transferResource.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentEntity } = resourceTeamAssignmentsSlice.actions;

// Selectors
export const selectAllAssignments = (state: RootState) => state.resourceTeamAssignments.entities;
export const selectAssignmentsByResource = (state: RootState, resourceId: string) =>
  state.resourceTeamAssignments.entities.filter(a => a.resourceId === resourceId);
export const selectAssignmentsByTeam = (state: RootState, teamId: string) =>
  state.resourceTeamAssignments.entities.filter(a => a.teamId === teamId);
export const selectActiveAssignments = (state: RootState) =>
  state.resourceTeamAssignments.entities.filter(a => a.endDate === null);
export const selectAssignmentsLoading = (state: RootState) => state.resourceTeamAssignments.loading;
export const selectAssignmentsError = (state: RootState) => state.resourceTeamAssignments.error;
export const selectCurrentAssignment = (state: RootState) => state.resourceTeamAssignments.currentEntity;

export default resourceTeamAssignmentsSlice.reducer;
